import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { FiPlus } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import FormCurrencyInput from "components/form/FormCurrencyInput";
import FormDatePicker from "components/form/FormDatePicker";
import FormSelect from "components/form/FormSelect";
import FormTextInput from "components/form/FormTextInput";
import { api, isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import {
  FIXED_MAXIMUM_INVESTMENT_AMOUNT,
  useProjectFormContext,
} from "pages/project/context/ProjectFormContext";
import { formatCurrencyDisplay } from "pages/project/validation/validateProjectStep";
import { extractIndustriesArray } from "utils/industriesListResponse";
import { buildQueryString } from "utils/helper";
import { PROJECT_STATUS_OPTIONS } from "utils/projectsList";

function industriesToSelectOptions(rows) {
  return (rows || [])
    .filter((row) => row && (row.isActive !== false) && (row.is_active !== false))
    .map((row) => {
      const id = row.id ?? row._id;
      if (id == null || id === "") return null;
      const label = String(row.name ?? "").trim() || "—";
      return { value: String(id), label };
    })
    .filter(Boolean);
}

export default function ProjectBasicsCard() {
  const navigate = useNavigate();
  const { values, setField, errors } = useProjectFormContext();
  const [industrySelectOptions, setIndustrySelectOptions] = useState([]);
  const [industriesLoading, setIndustriesLoading] = useState(true);

  const todayYmd = useMemo(() => dayjs().format("YYYY-MM-DD"), []);

  /** End date cannot be before today or before the campaign start (whichever is later). */
  const campaignEndMinYmd = useMemo(() => {
    const today = dayjs().startOf("day");
    let min = today;
    const start = values.campaignStartAt
      ? dayjs(values.campaignStartAt, "YYYY-MM-DD", true)
      : null;
    if (start?.isValid() && start.startOf("day").isAfter(min)) {
      min = start.startOf("day");
    }
    return min.format("YYYY-MM-DD");
  }, [values.campaignStartAt]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIndustriesLoading(true);
      try {
        const qs = buildQueryString({  status: "active" })
        const resp = await api.get(`${API_ENDPOINTS.industries}?${qs}`);
        if (!isSuccessResponse(resp)) {
          if (!cancelled) setIndustrySelectOptions([]);
          return;
        }
        const list = extractIndustriesArray(resp);
        if (!cancelled) setIndustrySelectOptions(industriesToSelectOptions(list));
      } catch {
        if (!cancelled) setIndustrySelectOptions([]);
      } finally {
        if (!cancelled) setIndustriesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const addIndustryAction = (
    <button
      type="button"
      onClick={() => navigate("/industries")}
      className="inline-flex items-center justify-center rounded-md border border-slate-200 p-1 text-text-600 transition hover:border-brand-500 hover:text-brand-900"
      title="Add or manage industries"
      aria-label="Add or manage industries"
    >
      <FiPlus className="h-4 w-4" aria-hidden />
    </button>
  );

  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 shadow-card md:p-6">
      <h2 className="mb-4 text-sm font-semibold text-text-900">Project details</h2>
      <div className="space-y-4">
        <FormTextInput
          label="Title"
          name="title"
          value={values.title}
          onChange={(e) => setField("title", e.target.value)}
          placeholder="e.g., Madison Square Mall"
          error={errors.title}
        />

        <div className="grid gap-4 md:grid-cols-2">
          <FormTextInput
            label="Issuer name"
            name="issuerName"
            value={values.issuerName}
            onChange={(e) => setField("issuerName", e.target.value)}
            placeholder="e.g., Enaara Developers"
            error={errors.issuerName}
          />
          <FormTextInput
            label="Type of eligible security offered"
            name="typeOfEligibleSecurityOffered"
            value={values.typeOfEligibleSecurityOffered}
            onChange={(e) => setField("typeOfEligibleSecurityOffered", e.target.value)}
            placeholder="e.g., Common Shares"
            error={errors.typeOfEligibleSecurityOffered}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <FormDatePicker
            label="Campaign start"
            name="campaignStartAt"
            value={values.campaignStartAt}
            onChange={(e) => setField("campaignStartAt", e.target.value)}
            min={todayYmd}
            error={errors.campaignStartAt}
            showShortcuts={false}
          />
          <FormDatePicker
            label="Campaign end"
            name="campaignEndAt"
            value={values.campaignEndAt}
            onChange={(e) => setField("campaignEndAt", e.target.value)}
            min={campaignEndMinYmd}
            error={errors.campaignEndAt}
            showShortcuts={false}
          />
          <FormSelect
            label="Industry"
            name="industry"
            value={values.industry}
            onChange={(e) => setField("industry", e.target.value)}
            options={industrySelectOptions}
            placeholder={industriesLoading ? "Loading industries…" : "Select industry"}
            disabled={industriesLoading}
            error={errors.industry}
            labelAction={addIndustryAction}
          />
          {/* <FormSelect
            label="Status"
            name="status"
            value={values.status}
            onChange={(e) => setField("status", e.target.value)}
            options={PROJECT_STATUS_OPTIONS}
            placeholder="Select status"
            error={errors.status}
          /> */}
          <FormCurrencyInput
            label="Total funds required"
            name="totalFundsRequired"
            value={values.totalFundsRequired}
            onChange={(e) => setField("totalFundsRequired", e.target.value)}
            placeholder="50,000"
            error={errors.totalFundsRequired}
          />
          <FormCurrencyInput
            label="Hard cap"
            name="hardCap"
            value={values.hardCap}
            onChange={(e) => setField("hardCap", e.target.value)}
            placeholder="500,000"
            error={errors.hardCap}
          />
          <FormCurrencyInput
            label="Minimum investment per investor"
            name="minimumInvestmentAmount"
            value={values.minimumInvestmentAmount}
            onChange={(e) => setField("minimumInvestmentAmount", e.target.value)}
            placeholder="250"
            error={errors.minimumInvestmentAmount}
          />
          <FormCurrencyInput
            label="Maximum investment per investor"
            name="maximumInvestmentAmount"
            value={formatCurrencyDisplay(values.maximumInvestmentAmount ?? FIXED_MAXIMUM_INVESTMENT_AMOUNT)}
            onChange={() => {}}
            placeholder="2,500"
            disabled
            readOnly
            error={errors.maximumInvestmentAmount}
          />
          <FormCurrencyInput
            label="Share price"
            name="sharePrice"
            value={values.sharePrice}
            onChange={(e) => setField("sharePrice", e.target.value)}
            placeholder="10.00"
            error={errors.sharePrice}
          />
        </div>
      </div>
    </section>
  );
}
